/**
 * Détail d'une campagne — portail client. Wrapper de queries autour de
 * `CampagneDetailView` (@repo/campagne) : ici toutes les queries sont
 * org-scopées, et le portail a les droits d'écriture.
 */
import {
  CampagneDetailView,
  CAPABILITES_CLIENT,
  useVueCampagne,
} from "@repo/campagne";
import { useQuery } from "convex/react";
import { useParams, useSearchParams } from "react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { FicheCandidatClient } from "./ParcoursDetail";

export function CampagneDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const campagneId = id as Id<"campagnes">;

  // Ce que la page affiche décide de ce qu'on charge : les passations d'un test
  // ne sont lues que par ses Résultats, son Matching et la table Candidats.
  const { chargerEvaluations } = useVueCampagne();

  const campagne = useQuery(api.campagnes.obtenirCampagne, { campagneId });
  const stats = useQuery(api.campagnes.obtenirStatsCampagne, { campagneId });

  const isEntStructCampagne =
    campagne?.evaluationsConfig?.some((e) => e.typeEval === "fairview") ??
    false;
  const isInbasketCampagne =
    campagne?.evaluationsConfig?.some((e) => e.typeEval === "inbasket") ?? false;
  const isPamiCampagne =
    campagne?.evaluationsConfig?.some(
      (e) => e.typeEval === "form" && e.template === "pami",
    ) ?? false;

  const evaluations = useQuery(
    api.evaluations.obtenirEvaluationsEntStructParCampagne,
    isEntStructCampagne && chargerEvaluations("entstruct") ? { campagneId } : "skip",
  );
  const evaluationsInbasket = useQuery(
    api.evaluations.obtenirEvaluationsInbasketParCampagne,
    isInbasketCampagne && chargerEvaluations("inbasket") ? { campagneId } : "skip",
  );
  const evaluationsPami = useQuery(
    api.evaluations.obtenirEvaluationsPamiParCampagne,
    isPamiCampagne && chargerEvaluations("pami") ? { campagneId } : "skip",
  );

  const isFitCulturelCampagne =
    campagne?.evaluationsConfig?.some(
      (e) => e.typeEval === "form" && e.template === "pc-fit",
    ) ?? false;

  // Query de niveau liste (ni notations ni projections lues) : sert uniquement à
  // savoir quels onglets référentiel ont quelque chose à montrer, cible par
  // cible.
  const referentiels = useQuery(
    api.referentiel.campagne.obtenirReferentielCampagne,
    { campagneId },
  );
  // Passations fit-culturel : chargées dès que la campagne fait passer le test.
  // Ne pas les conditionner au référentiel — la table de résultats fit culturel
  // se lit aussi sans référentiel configuré.
  const evaluationsFitCulturel = useQuery(
    api.evaluations.obtenirEvaluationsFitCulturelParCampagne,
    isFitCulturelCampagne && chargerEvaluations("fitculturel") ? { campagneId } : "skip",
  );

  const isCopsoq3Campagne =
    campagne?.evaluationsConfig?.some(
      (e) => e.typeEval === "form" && e.template === "copsoq3",
    ) ?? false;
  const evaluationsCopsoq3 = useQuery(
    api.evaluations.obtenirEvaluationsCopsoq3ParCampagne,
    isCopsoq3Campagne && chargerEvaluations("copsoq3") ? { campagneId } : "skip",
  );

  return (
    <CampagneDetailView
      campagneId={campagneId}
      campagne={campagne}
      stats={stats}
      evaluations={evaluations}
      evaluationsInbasket={evaluationsInbasket}
      evaluationsPami={evaluationsPami}
      evaluationsFitCulturel={evaluationsFitCulturel}
      evaluationsCopsoq3={evaluationsCopsoq3}
      referentiels={referentiels}
      capabilities={CAPABILITES_CLIENT}
      retourHref="/campagnes"
      modifierHref={`/campagnes/${campagneId}/modifier`}
      getParcoursHref={(parcoursId) =>
        `/p/${parcoursId}?from=/campagnes/${campagneId}`
      }
      renderFicheCandidat={(parcoursId) => (
        <FicheCandidatClient key={parcoursId} parcoursId={parcoursId} />
      )}
      ouvrirModaleEmail={searchParams.get("openEmailModal") === "true"}
      candidatPortailUrl={process.env.BUN_PUBLIC_APP_CANDIDATE_PORTAIL_URL}
    />
  );
}
